import DataTransformer from 'wixPaste/DataTransformer';
import _ from 'ckLodash';
import utils from 'utils/utils';

const supportedTextStyles = {
    'background-color': 'backColor',
    color: 'foreColor',
    'font-family': 'fontFamily',
    'font-size': 'fontSize',
    'font-style': 'italic',
    'font-weight': 'bold',
    'letter-spacing': 'letterSpacing',
    'text-align': 'justify{holder}',
    'text-decoration': 'underline',
    'text-shadow': 'textShadow'
};

const supportedListStyles = [
    'background-color',
    'color',
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'letter-spacing',
    'margin-left',
    'margin-right',
    'text-align',
    'text-decoration',
    'text-decoration-line',
    'text-decoration-color',
    'text-decoration-style',
    'text-shadow'
];

function createDivForComputingStyles(data) {
    //todo should create a sandboxed iframe instead of a div
    var div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.left = '-3150%';
    div.innerHTML = data;
    window.document.body.appendChild(div);
    return div;
}

function isList(node) {
    return node.nodeType === 1 && /^(?:ol|ul)$/.test(node.tagName.toLowerCase());
}

function generateModel(div) {
    const textNodes = [];
    const lists = {};
    const model = {
        textNodes,
        lists
    };
    let prefix = '';
    let treeWalker;
    if (utils.isIE()) {
        treeWalker = document.createTreeWalker(div, NodeFilter.SHOW_ALL, () => NodeFilter.FILTER_ACCEPT, false);
    } else {
        treeWalker = document.createTreeWalker(div, NodeFilter.SHOW_ALL);
    }
    while (treeWalker.nextNode()) {
        const currentNode = treeWalker.currentNode;
        if (currentNode.nodeType === 3) {
            if (utils.isWhiteSpace(currentNode.textContent)) {
                continue;
            }
            const computedStyle = _.pick(getComputedStyle(currentNode.parentNode), _.keys(supportedTextStyles));
            textNodes.push({
                prefix,
                computedStyle: computedStyle,
                textContent: currentNode.textContent
            });
            prefix += currentNode.textContent.replace(/[\s\ufeff]/g, '');
        } else if (isList(currentNode)) {
            const computedStyle = _.pick(getComputedStyle(currentNode), supportedListStyles);
            lists[prefix.length] = {computedStyle};
        }
    }
    return model;
}

function attachDomNodesToModel(editor, model, currentIndex = 0) {
    const {textNodes, lists} = model;
    let rangeForWalker = new CKEDITOR.dom.range(editor.editable());
    rangeForWalker.selectNodeContents(editor.editable());
    const domWalker = new CKEDITOR.dom.walker(rangeForWalker);
    let prefixLength = 0;
    let prefix = ''; //eslint-disable-line no-unused-vars
    let textNode = textNodes[currentIndex];
    while (domWalker.next() && textNode) {
        if (domWalker.current.type === CKEDITOR.NODE_TEXT) {
            let currentNode = domWalker.current;
            if (textNode.prefix.length === prefixLength && _.startsWith(clean(domWalker.current.$.textContent), clean(textNode.textContent))) {
                if (domWalker.current.$.textContent.length > textNode.textContent.length) {
                    const nextTextNode = textNodes[currentIndex + 1];
                    const splitForNext = (nextTextNode && domWalker.current.$.textContent.indexOf(nextTextNode.textContent)) || -1;
                    let split = _.max([textNode.textContent.length, splitForNext]);
                    domWalker.current.split(split);
                }
                textNode.relevantDomNode = currentNode;
                textNode = textNodes[++currentIndex];
            }
            prefixLength += utils.removeGuardsChars(currentNode.$.textContent).replace(/[\s\ufeff]/g, '').length;
            prefix += utils.removeGuardsChars(currentNode.$.textContent).replace(/[\s\ufeff]/g, '');
            while (textNode && textNode.prefix.length < prefixLength) {
                textNode = textNodes[++currentIndex];
            }
        } else if (lists[prefixLength] && isList(domWalker.current.$)) {
            lists[prefixLength].relevantDomNode = domWalker.current;
        }
    }
}

function getRelevantComputedStyle(editor, originalComputedStyle, relevantNode, supportedStyles) {
    const computedStyle = _.pick(editor.window.$.getComputedStyle(relevantNode), supportedStyles);
    return _.pickBy(originalComputedStyle, (val, key) => {
        if (key === 'text-decoration') {
            const regex = /none/;
            return (regex.test(val) && !regex.test(computedStyle[key])) || (regex.test(computedStyle[key]) && !regex.test(val));
        }
        return computedStyle[key] !== val;
    });
}

function modifySelectionToApplyStyleFromTextNodeModel(editor, currentNode) {
    const rangeForCommand = new CKEDITOR.dom.range(editor.editable());
    rangeForCommand.selectNodeContents(currentNode);
    rangeForCommand.select();
}

function execCommandsFromComputedStyles(editor, computedStyles) {
    _.forEach(computedStyles, (styleVal, styleName) => {
        const cmdName = supportedTextStyles[styleName].replace('{holder}', styleVal);
        const cmd = editor.getCommand(cmdName);
        if (cmd && cmd.exec) {
            cmd.exec(styleVal);
        }
        //editor.execCommand(cmdName, styleVal);
    });
}

function applyStyles(editor, modelArr, applyStyleFunc, callback, currentIndex = 0) {
    const modelNode = modelArr[currentIndex++];
    if (!modelNode) {
        callback();
        return;
    }
    if (modelNode.relevantDomNode) {
        applyStyleFunc(modelNode);
    }

    if (currentIndex % 10) {
        _.defer(() => applyStyles(editor, modelArr, applyStyleFunc, callback, currentIndex));
    } else {
        applyStyles(editor, modelArr, applyStyleFunc, callback, currentIndex);
    }
}

function applyStylesOnTextNodes(editor, textNodes, callback) {
    applyStyles(editor, textNodes, (textNode) => {
        const computedStyles = getRelevantComputedStyle(editor, textNode.computedStyle, textNode.relevantDomNode.$.parentNode, _.keys(supportedTextStyles));
        modifySelectionToApplyStyleFromTextNodeModel(editor, textNode.relevantDomNode);
        execCommandsFromComputedStyles(editor, computedStyles);
    }, callback);
}

function applyStylesOnLists(editor, lists, callback) {
    applyStyles(editor, lists, (list) => {
        const computedStyles = getRelevantComputedStyle(editor, list.computedStyle, list.relevantDomNode.$, supportedListStyles);
        _.forEach(computedStyles, (val, key) => {
            list.relevantDomNode.$.style[key] = val;
        });
    }, callback);
}

function stopEvent(evt) {
    evt.cancel();
    evt.stop();
}

function clean(str) {
    return utils.removeGuardsChars(str).replace(/[\s\ufeff]/g, '');
}

//function hideCkInstanceBodyToAvoidBlinkEfect(editor) {
//    const bodyStyle = editor.document.$.body.style;
//    const position = bodyStyle.position;
//    const left = bodyStyle.left;
//    bodyStyle.position = 'absolute';
//    bodyStyle.left = '-150%';
//    return {bodyStyle, position, left};
//}
//
//function showCkInstanceBody({bodyStyle, position, left}) {
//    bodyStyle.position = position;
//    bodyStyle.left = left;
//}

function afterAll(editor, div, listener, callback) {
    //showCkInstanceBody(hideCkRetVal);
    document.body.removeChild(div);
    listener.removeListener();
    editor.undoManager.unlock();
    editor.fire('change');
    editor.execCommand('autogrow');
    callback();
}

function cleanSetData(data, editor, setHtml) {
    const _data = new DataTransformer(data, true, editor).cleanExternalHtml()
        .fixOrphans()
        .trimEmptyLines()
        .fixDefaultThemesForTags()
        .fixListThemes()
        .getHtml();
    setHtml(editor, _data);
}

function setInvalidHtml(editor, data, callback, setHtml) {
    editor.undoManager.lock(true);
    cleanSetData(data, editor, setHtml);

    var div = createDivForComputingStyles(utils.removeGuardsChars(data));
    editor.execCommand('autogrow');
    let listener = editor.on('selectionChange', stopEvent, null, null, 5);
    //var hideCkRetVal = hideCkInstanceBodyToAvoidBlinkEfect(editor);
    _.defer(() => {
        var model = generateModel(div);
        attachDomNodesToModel(editor, model);
        const _afterAll = () => afterAll(editor, div, listener, callback);
        applyStylesOnTextNodes(editor, model.textNodes, () => applyStylesOnLists(editor, _.values(model.lists), _afterAll));
    });
}

export default setInvalidHtml;
